import supabaseCategoryService from './supabaseCategoryService'
import supabaseTagService from './supabaseTagService'
import supabaseArticleService from './supabaseArticleService'

class ArticleDataCacheService {
    constructor() {
        this.cache = new Map()
        this.sessionStorage = window.sessionStorage
        this.localStorage = window.localStorage
        this.cachePrefix = 'mahiru_article_'

        // 缓存配置
        this.cacheExpiry = {
            categories: 1000 * 60 * 60, // 分类缓存1小时
            tags: 1000 * 60 * 30, // 标签缓存30分钟
            articles: 1000 * 60 * 15, // 文章列表缓存15分钟
            pagination: 1000 * 60 * 10, // 分页数据缓存10分钟
            articleDetail: 1000 * 60 * 20 // 文章详情缓存20分钟
        }

        // 分页缓存配置
        this.maxPaginationPages = 20 // 最多缓存20页数据
        this.paginationCache = new Map()
    }

    /**
     * 获取缓存键
     */
    getCacheKey(type, identifier = '') {
        return `${this.cachePrefix}${type}${identifier ? '_' + identifier : ''}`
    }

    /**
     * 设置缓存数据
     */
    setCache(key, data, expiry = null) {
        const cacheData = {
            data,
            timestamp: Date.now(),
            expiry: expiry || (Date.now() + this.cacheExpiry.articles)
        }

        // 内存缓存
        this.cache.set(key, cacheData)

        // 持久化缓存（使用sessionStorage）
        try {
            this.sessionStorage.setItem(key, JSON.stringify(cacheData))
        } catch (error) {
            console.warn('缓存存储失败:', error)
        }
    }

    /**
     * 获取缓存数据
     */
    getCache(key) {
        // 先从内存获取
        const memoryCache = this.cache.get(key)
        if (memoryCache && memoryCache.expiry > Date.now()) {
            return memoryCache.data
        }

        // 从sessionStorage获取
        try {
            const cached = this.sessionStorage.getItem(key)
            if (cached) {
                const cacheData = JSON.parse(cached)
                if (cacheData.expiry > Date.now()) {
                    // 更新内存缓存
                    this.cache.set(key, cacheData)
                    return cacheData.data
                } else {
                    // 过期了，删除
                    this.sessionStorage.removeItem(key)
                }
            }
        } catch (error) {
            console.warn('读取缓存失败:', error)
        }

        return null
    }

    /**
     * 清除特定缓存
     */
    clearCache(key) {
        this.cache.delete(key)
        this.sessionStorage.removeItem(key)
    }

    /**
     * 清除所有缓存
     */
    clearAllCache() {
        // 清除内存缓存
        this.cache.clear()
        this.paginationCache.clear()

        // 清除sessionStorage中的相关缓存
        const keysToRemove = []
        for (let i = 0; i < this.sessionStorage.length; i++) {
            const key = this.sessionStorage.key(i)
            if (key && key.startsWith(this.cachePrefix)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => this.sessionStorage.removeItem(key))

        console.log('🗑️ 文章数据缓存已清除')
    }

    /**
     * 获取分类数据（带缓存）
     */
    async getCategories(forceReload = false) {
        const cacheKey = this.getCacheKey('categories')

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的分类数据:', cached.length)
                return cached
            }
        }

        try {
            console.log('📡 加载分类数据...')
            const categories = await supabaseCategoryService.list()

            // 缓存数据
            this.setCache(cacheKey, categories, Date.now() + this.cacheExpiry.categories)

            console.log('✅ 分类数据加载完成:', categories.length)
            return categories
        } catch (error) {
            console.error('❌ 加载分类数据失败:', error)
            throw error
        }
    }

    /**
     * 获取标签数据（带缓存）
     */
    async getTags(forceReload = false) {
        const cacheKey = this.getCacheKey('tags')

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的标签数据:', cached.length)
                return cached
            }
        }

        try {
            console.log('📡 加载标签数据...')
            const tags = await supabaseTagService.list()

            // 缓存数据
            this.setCache(cacheKey, tags, Date.now() + this.cacheExpiry.tags)

            console.log('✅ 标签数据加载完成:', tags.length)
            return tags
        } catch (error) {
            console.error('❌ 加载标签数据失败:', error)
            throw error
        }
    }

    /**
     * 获取标签分页数据（带缓存）
     */
    async getTagsPaginated(options = {}, forceReload = false) {
        const { limit = 12, cursor = null } = options
        const cacheKey = this.getCacheKey('tags_paginated', `${limit}_${cursor ? JSON.stringify(cursor) : 'first'}`)

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的标签分页数据')
                return cached
            }
        }

        try {
            console.log('📡 加载标签分页数据...')
            const result = await supabaseTagService.listByUsageCursor(options)

            // 缓存数据
            this.setCache(cacheKey, result, Date.now() + this.cacheExpiry.tags)

            console.log('✅ 标签分页数据加载完成:', result.items.length)
            return result
        } catch (error) {
            console.error('❌ 加载标签分页数据失败:', error)
            throw error
        }
    }

    /**
     * 获取文章列表（带缓存）
     */
    async getArticles(options = {}, forceReload = false) {
        const { limit, offset, categoryId, tagId, tagIds } = options
        const cacheKey = this.getCacheKey('articles', this.generateArticlesCacheKey(options))

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的文章列表数据:', cached.length)
                return cached
            }
        }

        try {
            console.log('📡 加载文章列表数据...')
            const articles = await supabaseArticleService.listPublished(options)

            // 缓存数据
            this.setCache(cacheKey, articles, Date.now() + this.cacheExpiry.articles)

            console.log('✅ 文章列表数据加载完成:', articles.length)
            return articles
        } catch (error) {
            console.error('❌ 加载文章列表数据失败:', error)
            throw error
        }
    }

    /**
     * 获取文章分页数据（带缓存）
     */
    async getArticlesPaginated(options = {}, forceReload = false) {
        const { limit = 20, cursor = null, categoryId, tagId, tagIds } = options
        const cacheKey = this.getCacheKey('articles_paginated', this.generateArticlesPaginatedCacheKey(options))

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的文章分页数据:', cached.items?.length || 0)
                return cached
            }
        }

        try {
            console.log('📡 加载文章分页数据...')
            const result = await supabaseArticleService.listPublishedByCursor(options)

            // 缓存数据
            this.setCache(cacheKey, result, Date.now() + this.cacheExpiry.pagination)

            // 同时缓存到分页缓存中
            this.cachePaginationData(options, result)

            console.log('✅ 文章分页数据加载完成:', result.items?.length || 0)
            return result
        } catch (error) {
            console.error('❌ 加载文章分页数据失败:', error)
            throw error
        }
    }

    /**
     * 获取文章详情（带缓存）
     */
    async getArticleDetail(articleId, forceReload = false) {
        const cacheKey = this.getCacheKey('article_detail', articleId)

        if (!forceReload) {
            const cached = this.getCache(cacheKey)
            if (cached) {
                console.log('✅ 使用缓存的文章详情:', articleId)
                return cached
            }
        }

        try {
            console.log('📡 加载文章详情:', articleId)
            const article = await supabaseArticleService.getPublicById(articleId)

            if (article) {
                // 缓存数据
                this.setCache(cacheKey, article, Date.now() + this.cacheExpiry.articleDetail)
                console.log('✅ 文章详情加载完成:', article.title)
            }

            return article
        } catch (error) {
            console.error('❌ 加载文章详情失败:', error)
            throw error
        }
    }

    /**
     * 生成文章列表缓存键
     */
    generateArticlesCacheKey(options) {
        const { limit, offset, categoryId, tagId, tagIds } = options
        const parts = []

        if (limit) parts.push(`limit_${limit}`)
        if (offset) parts.push(`offset_${offset}`)
        if (categoryId) parts.push(`cat_${categoryId}`)
        if (tagId) parts.push(`tag_${tagId}`)
        if (tagIds && tagIds.length > 0) parts.push(`tags_${tagIds.sort().join(',')}`)

        return parts.join('_') || 'default'
    }

    /**
     * 生成文章分页缓存键
     */
    generateArticlesPaginatedCacheKey(options) {
        const { limit, cursor, categoryId, tagId, tagIds } = options
        const parts = []

        if (limit) parts.push(`limit_${limit}`)
        if (cursor) parts.push(`cursor_${JSON.stringify(cursor)}`)
        if (categoryId) parts.push(`cat_${categoryId}`)
        if (tagId) parts.push(`tag_${tagId}`)
        if (tagIds && tagIds.length > 0) parts.push(`tags_${tagIds.sort().join(',')}`)

        return parts.join('_') || 'default'
    }

    /**
     * 缓存分页数据到专用缓存
     */
    cachePaginationData(options, result) {
        const baseKey = this.generatePaginationBaseKey(options)

        // 清理旧的分页缓存（保持最多maxPaginationPages页）
        if (this.paginationCache.size >= this.maxPaginationPages) {
            const oldestKey = this.paginationCache.keys().next().value
            this.paginationCache.delete(oldestKey)
        }

        const paginationData = {
            ...result,
            timestamp: Date.now(),
            expiry: Date.now() + this.cacheExpiry.pagination
        }

        this.paginationCache.set(baseKey, paginationData)
    }

    /**
     * 生成分页基础键
     */
    generatePaginationBaseKey(options) {
        const { categoryId, tagId, tagIds } = options
        const parts = ['pagination']

        if (categoryId) parts.push(`cat_${categoryId}`)
        if (tagId) parts.push(`tag_${tagId}`)
        if (tagIds && tagIds.length > 0) parts.push(`tags_${tagIds.sort().join(',')}`)

        return parts.join('_')
    }

    /**
     * 预加载常用数据
     */
    async preloadCommonData() {
        try {
            console.log('🚀 开始预加载文章相关数据...')

            const promises = [
                this.getCategories(),
                this.getTags(),
                this.getArticlesPaginated({ limit: 20 }) // 预加载第一页文章
            ]

            await Promise.allSettled(promises)
            console.log('✅ 文章相关数据预加载完成')

        } catch (error) {
            console.warn('⚠️ 文章数据预加载失败:', error)
        }
    }

    /**
     * 智能缓存清理
     */
    smartCleanup() {
        const now = Date.now()
        const keysToDelete = []

        // 清理过期的内存缓存
        for (const [key, value] of this.cache.entries()) {
            if (value.expiry <= now) {
                keysToDelete.push(key)
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key))

        // 清理过期的分页缓存
        const paginationKeysToDelete = []
        for (const [key, value] of this.paginationCache.entries()) {
            if (value.expiry <= now) {
                paginationKeysToDelete.push(key)
            }
        }

        paginationKeysToDelete.forEach(key => this.paginationCache.delete(key))

        if (keysToDelete.length > 0 || paginationKeysToDelete.length > 0) {
            console.log(`🧹 清理了 ${keysToDelete.length + paginationKeysToDelete.length} 个过期缓存`)
        }
    }

    /**
     * 获取缓存统计信息
     */
    getCacheStats() {
        return {
            memoryCacheSize: this.cache.size,
            paginationCacheSize: this.paginationCache.size,
            maxPaginationPages: this.maxPaginationPages,
            cacheExpiry: this.cacheExpiry
        }
    }

    /**
     * 批量预取数据
     */
    async batchPreload(requests = []) {
        const promises = requests.map(async (request) => {
            try {
                switch (request.type) {
                    case 'categories':
                        return await this.getCategories(request.forceReload)
                    case 'tags':
                        return await this.getTags(request.forceReload)
                    case 'articles':
                        return await this.getArticles(request.options, request.forceReload)
                    case 'articlesPaginated':
                        return await this.getArticlesPaginated(request.options, request.forceReload)
                    case 'articleDetail':
                        return await this.getArticleDetail(request.articleId, request.forceReload)
                    default:
                        console.warn('未知的预加载类型:', request.type)
                        return null
                }
            } catch (error) {
                console.warn(`预加载 ${request.type} 失败:`, error)
                return null
            }
        })

        const results = await Promise.allSettled(promises)
        const successful = results.filter(r => r.status === 'fulfilled').length

        console.log(`📦 批量预加载完成: ${successful}/${requests.length} 成功`)
        return results
    }
}

export default new ArticleDataCacheService() 